/**
 * Requirement filter panel — free-text "内容" plus user-added key/value conditions.
 * Value options are dynamically extracted from requirement data.
 */

import { useMemo } from 'react';
import { Card, Select, Input, Button, Typography } from 'antd';
import { FilterOutlined, PlusOutlined, CloseOutlined } from '@ant-design/icons';
import { STATUS_FILTER_OPTIONS } from '@/constants/requirements';
import type { Requirement } from '@/types';
import { useT } from '@/i18n';

const { Text } = Typography;

/** Requirement fields available as filter keys. */
export const FILTER_FIELDS = [
  { key: 'owner', label: '责任人' },
  { key: 'deliveryStage', label: '交付阶段' },
  { key: 'category', label: '一级分类' },
  { key: 'category2', label: '二级分类' },
  { key: 'priority', label: '优先级' },
  { key: 'deliverables', label: '交付物' },
  { key: 'verificationMethod', label: '验证方法' },
  { key: 'linkedPackageName', label: '需求交付首版软件' },
  { key: 'status', label: '状态' },
  { key: 'bspTag', label: 'TAG' },
  { key: 'bspTag1', label: 'TAG1' },
  { key: 'bspTag2', label: 'TAG2' },
] as const;

export type FilterFieldKey = (typeof FILTER_FIELDS)[number]['key'];

export interface FilterCondition {
  id: string;
  field: FilterFieldKey;
  values: string[];
}

export interface RequirementFilters {
  keyword: string;
  conditions: FilterCondition[];
}

export const EMPTY_FILTERS: RequirementFilters = {
  keyword: '',
  conditions: [],
};

interface RequirementFilterProps {
  filters: RequirementFilters;
  onChange: (filters: RequirementFilters) => void;
  /** Requirements data used to extract dynamic value options. */
  requirements: Requirement[];
}

const RequirementFilter: React.FC<RequirementFilterProps> = ({
  filters,
  onChange,
  requirements,
}) => {
  const { t } = useT();
  const valueOptions = useMemo(() => {
    const map = {} as Record<FilterFieldKey, { value: string; label: string }[]>;

    for (const { key } of FILTER_FIELDS) {
      if (key === 'status') {
        map[key] = STATUS_FILTER_OPTIONS;
        continue;
      }
      const values = new Set<string>();
      for (const req of requirements) {
        const value = req[key];
        if (value) values.add(String(value));
      }
      map[key] = Array.from(values)
        .sort((a, b) => a.localeCompare(b, 'zh-CN'))
        .map((v) => ({ value: v, label: v }));
    }
    return map;
  }, [requirements]);

  const hasFilters =
    filters.keyword.trim().length > 0 ||
    filters.conditions.some((c) => c.values.length > 0);

  const updateCondition = (id: string, patch: Partial<FilterCondition>) => {
    onChange({
      ...filters,
      conditions: filters.conditions.map((c) => (c.id === id ? { ...c, ...patch } : c)),
    });
  };

  const addCondition = () => {
    const used = new Set(filters.conditions.map((c) => c.field));
    const nextField = FILTER_FIELDS.find((f) => !used.has(f.key))?.key;
    if (!nextField) return;
    onChange({
      ...filters,
      conditions: [
        ...filters.conditions,
        {
          id: `cond-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
          field: nextField,
          values: [],
        },
      ],
    });
  };

  const removeCondition = (id: string) => {
    onChange({
      ...filters,
      conditions: filters.conditions.filter((c) => c.id !== id),
    });
  };

  return (
    <Card size="small" style={{ width: 220 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 12 }}>
        <FilterOutlined />
        <Text strong>{t('过滤条件')}</Text>
      </div>

      <div style={{ marginBottom: 12 }}>
        <Text type="secondary" style={{ fontSize: 12 }}>{t('内容')}</Text>
        <Input
          allowClear
          autoComplete="off"
          placeholder={t('搜索内容')}
          style={{ width: '100%', marginTop: 4 }}
          size="small"
          value={filters.keyword}
          onChange={(e) => onChange({ ...filters, keyword: e.target.value })}
        />
      </div>

      {filters.conditions.map((condition) => {
        const fieldOptions = FILTER_FIELDS.filter(
          (f) =>
            f.key === condition.field ||
            !filters.conditions.some((c) => c.id !== condition.id && c.field === f.key)
        ).map((f) => ({ value: f.key, label: t(f.label) }));

        return (
          <div
            key={condition.id}
            style={{
              border: '1px solid #f0f0f0',
              borderRadius: 6,
              padding: 8,
              marginBottom: 8,
            }}
          >
            <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
              <Select
                size="small"
                style={{ flex: 1, minWidth: 0 }}
                value={condition.field}
                options={fieldOptions}
                onChange={(field) => updateCondition(condition.id, { field, values: [] })}
              />
              <Button
                size="small"
                type="text"
                icon={<CloseOutlined />}
                onClick={() => removeCondition(condition.id)}
              />
            </div>
            <Select
              mode="multiple"
              allowClear
              showSearch
              size="small"
              placeholder={t('选择值')}
              style={{ width: '100%', marginTop: 4 }}
              value={condition.values}
              options={valueOptions[condition.field]}
              onChange={(values) => updateCondition(condition.id, { values })}
              maxTagCount="responsive"
              notFoundContent={
                <span style={{ fontSize: 12, color: '#999' }}>{t('暂无可选值')}</span>
              }
            />
          </div>
        );
      })}

      <Button
        size="small"
        block
        icon={<PlusOutlined />}
        onClick={addCondition}
        disabled={filters.conditions.length >= FILTER_FIELDS.length}
        style={{ marginBottom: hasFilters ? 8 : 0 }}
      >
        {t('添加过滤条件')}
      </Button>

      {hasFilters && (
        <Button size="small" block onClick={() => onChange(EMPTY_FILTERS)}>
          {t('重置')}
        </Button>
      )}
    </Card>
  );
};

export default RequirementFilter;
